/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.37.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { appHistoryPush } from '@modules/app/appHistory';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { ExternalRoutePath, openExternalRoute } from '@routes/constants/externalRoutes';
import { IRouteParams, RoutePath, SUBSCRIBER_ID } from '@routes/constants/routesConstants';
import { stringFormat } from '@utils/stringFormat';

import { icons } from '../../../../../assets';
import { openMobileChangerole } from '../../../../Table/actions/tableActions';
import { TTableButtonsGetTranslation, TTableButtonsLogBusinessEvent } from '../useTableFooterButtons';


const useTableFooterButtonAccessLevel = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
) => {
    const dispatch = useDispatch();

    const { clientId } = useParams<IRouteParams>();

    const isWizardChangeRoleEnabled =
        useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardChangeRole));

    return {
        className: 'table-footer__link link-private',
        dataName: 'private',
        icon: icons.loginIcon,
        id: AccessComponentKeyEnum.FooterButtonAccessLevels,
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonAccessLevel,
        ...getTranslation(AccessComponentKeyEnum.FooterButtonAccessLevels),

        onClick: useCallback(() => {
            dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.CHANGE_ROLE }));
            logBusinessEvent(ELogBusinessEventName.USER_ACCESS_LEVEL_MASTER_TO_STEP_1, ELogBusinessEventType.common);
            if (clientId) {
                isWizardChangeRoleEnabled
                    ? appHistoryPush(RoutePath.wizardChangeRoleFromSubscriberId.replace(SUBSCRIBER_ID, clientId))
                    : openExternalRoute(stringFormat(ExternalRoutePath.mobileRoleId, clientId));
            } else {
                dispatch(openMobileChangerole())
            }
        }, [dispatch, logBusinessEvent, clientId, isWizardChangeRoleEnabled]),
    };
};

export default useTableFooterButtonAccessLevel;
